import type {
    BroadcastConfig,
    PlayerConfigCommon,
    PlayerCustomEvent,
    StreamingProvider,
    StripChatPlayerConfig,
} from '$lib/services/streaming/types';

import type { Broadcaster as Broadcast, Player } from '@components/ristretto-player';
import { PLAYER_CREDENTIALS, PLAYER_USERNAME } from '@components/room/constants';
import { streamingLoggerStore } from '@components/room/streaming-debugger/stores/logger';
import { PLAYER_APP_KEY } from '@shared/constants';
import { LogType } from '@shared/types';
import { StreamProvider, StreamStatus } from '@shared/types/room';
import { isFirefox } from '@utils/device';

import { getStripchatBroadcastConfig } from './config';

// eslint-disable-next-line
// @ts-ignore
import { browser } from '$app/environment';

type PlayerModule = { new (): Player };
type BroadcasterModule = { new(): Broadcast };

export default class Stripchat implements StreamingProvider {
    broadcastModule: BroadcasterModule | null;
    playerModule: PlayerModule | null;

    broadcast: Broadcast | null;
    player: Player | null;

    isModulesLoaded: boolean;

    broadcastConfig?: BroadcastConfig;

    constructor() {
        this.broadcastModule = null;
        this.playerModule = null;

        this.broadcast = null;
        this.player = null;
        this.isModulesLoaded = false;
    }

    public getType() {
        return StreamProvider.StripChat;
    }

    public async startBroadcast(config: BroadcastConfig) {

        streamingLoggerStore.log(
            { type: LogType.AppBroadcast, message: 'StreamingProvider: Stripchat: getConfig' }
        );
        const configResp = await this.getBroadcastConfigWithRetry();

        if (!configResp) {
            console.error('No stripchat stream config');
            return;
        }

        const { id, origin, token } = configResp;

        streamingLoggerStore.log(
            { type: LogType.AppBroadcast, message: 'StreamingProvider: Stripchat: loadModule' },
            {
                streamId: id,
                streamOrigin: origin,
            },
            { includeExtendedData: true }
        );
        await this.loadModules();

        if (!this.broadcastModule) {
            return;
        }

        const {
            mediaStream,
            loggerCallback,
            onEvent,
        } = config;

        this.broadcast = new this.broadcastModule();

        this.broadcast.setStreamId(id || '');
        this.broadcast.setWSServerUrl(origin || '');
        this.broadcast.setAppKey(PLAYER_APP_KEY);
        this.broadcast.setStreamToken(token || '');
        this.broadcast.setMediaStream(mediaStream);

        this.broadcast.setConfig({
            ...this.buildPeerConnectionConfig(origin || '', 'broadcaster'),
        });

        if (loggerCallback) {
            this.broadcast.registerLoggerCallback(loggerCallback);
        }

        // eslint-disable-next-line
        // @ts-ignore
        this.broadcast.addEventListener('lifecycleEvent', (event: PlayerCustomEvent<StreamStatus> = {}) => {
            const { eventType } = event?.detail || {};

            if (!eventType) {
                streamingLoggerStore.log({
                    type: LogType.AppBroadcast,
                    message: 'StreamingProvider: Stripchat: lifecycleEvent: no event type',
                });
                console.error('no eventType');
                return;
            }

            if (onEvent) {
                onEvent(eventType);
            }
        });

        this.broadcast.start();
        this.broadcastConfig = config;

        return this.broadcast;
    }

    private async getBroadcastConfigWithRetry() {
        let retryCount = 3;
        while (retryCount) {
            const publishUrl = await getStripchatBroadcastConfig();
            if (publishUrl) {
                return publishUrl;
            }
            retryCount -= 1;
        }
        console.error('getConfigWithRetry: retry exceed');
        return null;
    }

    public async restartBroadcast() {
        streamingLoggerStore.log({
            type: LogType.AppBroadcast,
            message: 'StreamingProvider: Stripchat: restartBroadcast',
        });
        if (!this.broadcastConfig) {
            streamingLoggerStore.log({
                type: LogType.AppBroadcast,
                message: 'StreamingProvider: Stripchat: No broadcast config',
            });
            console.error('No broadcast config');
            return;
        }
        this.stopBroadcast();
        await this.startBroadcast(this.broadcastConfig);
    }

    public stopBroadcast() {
        if (this.broadcast) {
            streamingLoggerStore.log({
                type: LogType.AppBroadcast,
                message: 'StreamingProvider: Stripchat: stopBroadcast',
            });
            this.broadcast.stop();
            this.broadcast.destroy();
            this.broadcast = null;
        }
    }

    public async startDelivery(config: StripChatPlayerConfig & PlayerConfigCommon) {
        streamingLoggerStore.log(
            {
                type: LogType.AppDelivery,
                message: 'StreamingProvider: Stripchat: startDelivery',
            },
            {
                streamId: config.id,
                streamOrigin: config.origin,
            },
            { includeExtendedData: true }
        );
        await this.loadModules();

        if (!this.playerModule) {
            return;
        }

        const { id, origin, video, loggerCallback, onEvent } = config;

        this.player = new this.playerModule();
        this.player.mute = function () {
            if (this.videoElement) {
                this.videoElement.muted = true;
            }
        };
        this.player.unmute = function () {
            if (this.videoElement) {
                this.videoElement.muted = false;
            }
        };

        this.player.setStreamId(id);
        this.player.setWSServerUrl(origin);
        this.player.setVideoElement(video);
        this.player.setAppKey(PLAYER_APP_KEY);

        if (loggerCallback) {
            this.player.registerLoggerCallback(loggerCallback);
        }

        // и тут
        if (origin) {
            this.player.setConfig({ ...this.buildPeerConnectionConfig(origin, 'player') });
        }

        // этот кусок с лисенером можно двинуть после createPlayer, чтобы словить контекст румы/юзера для лога
        // eslint-disable-next-line
        // @ts-ignore
        this.player.addEventListener('lifecycleEvent', (event: PlayerCustomEvent<PlayerStatus> = {}) => {
            const { eventType } = event?.detail || {};

            if (!eventType) {
                streamingLoggerStore.log({
                    type: LogType.AppDelivery,
                    message: 'StreamingProvider: Stripchat: lifecycleEvent: no event type',
                });
                console.error('no eventType');
                return;
            }

            if (onEvent) {
                onEvent(eventType);
            }
        });

        this.player.start();

        return this.player;
    }

    public stopDelivery() {
        const player = this.player;
        if (player) {
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'StreamingProvider: Stripchat: stopDelivery',
            });
            player.stop();
            player.detachVideoElement();
            player.destroy();
            this.player = null;
        }
    }

    public destroy() {
        this.stopBroadcast();
        this.stopDelivery();
    }

    private async loadModules() {
        const modulesNotLoaded = browser && !this.broadcastModule && !this.playerModule;

        if (modulesNotLoaded) {
            const module = await import('@components/ristretto-player/module');

            this.broadcastModule = module.Broadcaster as unknown as BroadcasterModule;
            this.playerModule = module.Player as unknown as PlayerModule;
        }
    }

    private buildPeerConnectionConfig(streamOrigin: string, type: 'player' | 'broadcaster') {
        if (window.localStorage.getItem(`qa_error_${type}`)) {
            return {
                websocketTransportConfig: {
                    maxConnectRetries: 1,
                    pendingStateConnectionTimeout: 1,
                },
            };
        }

        if (streamOrigin?.includes('flirtify.com')) {
            return {
                broadcastDispatcherConfig: {
                    metadataReconnectMaxAttempts: 20,
                },
                streamDispatcherConfig: {
                    streamRestartMaxAttempts: 20,
                },
                peerConnectionConfig: isFirefox() ? {} : {
                    iceServers: [
                        {
                            urls: this.buildIceServerUrl(streamOrigin),
                            username: PLAYER_USERNAME,
                            credential: PLAYER_CREDENTIALS,
                        },
                    ],
                },
            };
        }

        return {
            peerConnectionConfig: {},
        };
    }

    private buildIceServerUrl(origin: string): string[] {
        if (origin?.includes('01')) {
            return [
                'turn:stream01-turn.flirtify.com:80?transport=udp',
                'turn:stream01-turn.flirtify.com:80?transport=tcp',
            ];
        } else if (origin?.includes('02')) {
            return [
                'turn:stream02-turn.flirtify.com:80?transport=udp',
                'turn:stream02-turn.flirtify.com:80?transport=tcp',
            ];
        }

        return [
            'turn:stream01-turn.flirtify.com:80?transport=udp',
            'turn:stream01-turn.flirtify.com:80?transport=tcp',
        ];
    }
}
